import { FeeIntelligence } from './feeIntelligence';
import { comNfPropria } from './impostoNfContaDiretaPolicy';
import { AgenciaOrcamentoPolicy } from './agenciaOrcamentoPolicy';
import { cambioBrlLinha, normalizarMoeda, temCambioProprio } from './moedaFornecedorPolicy';

// "Valor exato em reais" (Rafael 02/10, decisão A — contrato comum com a API e o Excel):
// - R$ de cada linha por etapa: linha em Real = valor digitado; moeda própria estrangeira = total ×
//   câmbio próprio da etapa; legado = total na moeda do orçamento × câmbio do orçamento. Só o R$ da
//   linha é arredondado (2 casas).
// - Subtotal do grupo, TOTAL ACCESS, Resumo, painel e valor total em R$ = soma dos R$ das linhas (+ R$
//   das automáticas). Valor na moeda do orçamento = R$ ÷ câmbio, arredondado UMA vez no fim.
// - FEE: % × base em R$ (arredondado a 2 casas); na moeda do orçamento = FEE R$ ÷ câmbio.
// Modo "R$ exato" (gravação) só existe no orçamento em moeda estrangeira com ao menos uma linha de
// moeda própria (regra 6: legado e orçamento em Real gravam exatamente como antes).

export const MOMENTOS_BRL = { PreEvento: 'pre_evento', Negociado: 'negociado', Fechamento: 'fechamento' };
export const ETAPAS_BRL = ['PreEvento', 'Negociado', 'Fechamento'];

const numero = (valor) => {
    if (typeof valor === 'number') return Number.isFinite(valor) ? valor : 0;
    if (valor === null || valor === undefined) return 0;
    const texto = String(valor).trim();
    if (!texto) return 0;
    // Mesma leitura de util.virgulaPonto (parseFloat), usada pela grade até aqui.
    const convertido = parseFloat(texto.includes(',') ? texto.replace(/\./g, '').replace(',', '.') : texto);
    return Number.isFinite(convertido) ? convertido : 0;
};

// Arredondamento a 2 casas igual ao round() do PHP (meio para longe do zero, com pré-arredondamento
// a 15 dígitos): 29247,835 vira 29247,84 nos dois lados.
export const centavos = (valor) => {
    const n = numero(valor);
    if (n === 0) return 0;
    const escalado = Number((Math.abs(n) * 100).toPrecision(15));
    return Math.sign(n) * Math.round(escalado) / 100;
};

// 'pre'|'pre_evento'|'PreEvento' → PreEvento; 'neg'|'negociado'|'Negociado' → Negociado; demais → Fechamento.
export const etapaBrl = (etapa = 'PreEvento') => {
    const e = String(etapa).toLowerCase();
    if (['pre', 'pre_evento', 'preevento'].includes(e)) return 'PreEvento';
    if (['neg', 'negociado'].includes(e)) return 'Negociado';
    return 'Fechamento';
};

const cambioValido = (orcamento = {}) => {
    const c = numero(orcamento?.cambio);
    return c > 0 ? c : null;
};

// Linhas do orçamento (grupos com subitem ou lista plana com `superior`).
const linhasDoOrcamento = (orcamento = {}) => (orcamento?.orcamentoitem || []).flatMap((item) => {
    if (Array.isArray(item?.subitem)) return item.subitem;
    return item && item.superior ? [item] : [];
});

// Modo R$ exato: orçamento estrangeiro, câmbio válido e ao menos uma linha com moeda própria.
export function orcamentoBrlExato(orcamento = {}) {
    if (normalizarMoeda(orcamento?.moeda) === 'real' || cambioValido(orcamento) === null) return false;
    return linhasDoOrcamento(orcamento).some((linha) => !!linha && temCambioProprio(linha));
}

// Long Ping em Real (D-0119, "tem que bater no centavo com a planilha"): a linha marcada entra nas somas
// pelo total SEM arredondar (refeito do unitário), e o total arredonda uma vez — como a API
// (OrcamentoChiesiImpostoPolicy::calcularValorVigente com $somaSemArredondar). Marca posta no calculaTotalOrcamento2.
export const CAMPO_SOMA_SEM_ARREDONDAR = 'somaSemArredondar';

// Orçamento em moeda estrangeira com câmbio válido: toda EXIBIÇÃO em R$ usa a conta exata (item 3,
// pós-QA 02/10), inclusive no legado — sem mudar o que é gravado.
export function orcamentoEstrangeiro(orcamento = {}) {
    return normalizarMoeda(orcamento?.moeda) !== 'real' && cambioValido(orcamento) !== null;
}

// Anotação SÓ DE EXIBIÇÃO na linha de FEE (feeBaseInternacionalPolicy.prepararExibicaoBrl): FEE R$ =
// % × base em R$, por momento. Nunca vai para o payload (a tela grava `dados`, não a cópia de exibição).
export const CAMPO_VALOR_BRL_EXIBICAO = 'valorBrlExibicao';

// Linha de FEE calculado (regra percentual, FEE AGÊNCIA): o R$ dela vem do total SEM arredondar × câmbio.
export const linhaDeFeeBrl = (linha = {}) => {
    const produto = linha?.produto ?? {};
    return FeeIntelligence.isFeeCalculatorItem(produto) || AgenciaOrcamentoPolicy.isFeeAgenciaGlobal(produto);
};

// Total da linha na etapa SEM arredondar (mesma fórmula da grade, CadastroOrcamento.calcularTotalMomentoSubitem):
// FEE de regra = base × % ÷ (1 − % Imposto NF); linha de imposto (1945) = subtotal × (1 + % Serviço);
// demais = subtotal × (1 + % Serviço + % Impostos), com gross-up do % Imposto NF.
export function totalBrutoLinha(subitem = {}, etapa = 'PreEvento') {
    const momento = etapaBrl(etapa);
    const fee = FeeIntelligence.calculateAgencyFeeLineTotalRaw(subitem, MOMENTOS_BRL[momento]);
    if (fee !== null) return fee;
    const subtotal = numero(subitem[`quantidade${momento}`]) * numero(subitem[`diaria${momento}`]) * numero(subitem[`valorUnitario${momento}`]);
    const servico = numero(subitem[`servico${momento}`]);
    const ehLinhaImposto = subitem.impostoNfAccessManualAgencia || String(subitem?.produto?.codigo ?? '').trim() === '1945';
    if (ehLinhaImposto) return subtotal * (1 + (servico / 100));
    const impostos = numero(subitem.impostoPorcentagemItem);
    // D-0112: % Imposto NF na conta da linha (direta = × (1 + a)).
    return comNfPropria(subtotal * (1 + (servico / 100) + (impostos / 100)), subitem.imposto, subitem);
}

// Regra 2 + contrato v2 B (planilha): R$ da linha na etapa SEM arredondar (total × câmbio; Real = valor
// digitado); só a exibição arredonda. `exato`: a linha de FEE usa o total sem arredondar (% × base R$).
export function valorBrlLinha(linha = {}, etapa = 'PreEvento', cambioGlobal = 1, { exato = false } = {}) {
    const momento = etapaBrl(etapa);
    const global = numero(cambioGlobal) > 0 ? numero(cambioGlobal) : 1;
    const exibicao = linha?.[CAMPO_VALOR_BRL_EXIBICAO]?.[MOMENTOS_BRL[momento]];
    if (typeof exibicao === 'number' && Number.isFinite(exibicao) && !temCambioProprio(linha)) return exibicao;
    if (linha?.[CAMPO_SOMA_SEM_ARREDONDAR] && !temCambioProprio(linha)) return totalBrutoLinha(linha, momento) * global;
    if (exato && !temCambioProprio(linha) && linhaDeFeeBrl(linha)) return totalBrutoLinha(linha, momento) * global;
    return numero(linha?.[`total${momento}`]) * cambioBrlLinha(linha, momento, global);
}

// D-0132 (Rafael, 30/10): em orçamento estrangeiro o FEE da Agência (130, regra percentual, fees de
// catálogo Sanofi/AbbVie) aparece só em R$. Só exibição: o gravado segue na moeda do orçamento.
export const linhaFeeSoEmReal = (linha = {}, emMoedaEstrangeira = false) => !!emMoedaEstrangeira
    && !temCambioProprio(linha)
    && (linhaDeFeeBrl(linha) || AgenciaOrcamentoPolicy.isFeeDeCatalogo(linha?.produto ?? {}));

// R$ que a linha de FEE mostra e que entra nos totais: Fechamento recalculado pelo exterior > % × base R$
// (`exibicao`, prepararExibicaoBrl) > total sem arredondar × câmbio.
export function feeBrlExibido(linha = {}, etapa = 'PreEvento', cambioGlobal = 1, exibicao = null) {
    const momento = etapaBrl(etapa);
    const totalBrl = linha?.fechamento_exibicao?.total_brl;
    if (momento === 'Fechamento' && totalBrl !== undefined && totalBrl !== null) return numero(totalBrl);
    const anotado = exibicao?.[MOMENTOS_BRL[momento]];
    if (typeof anotado === 'number' && Number.isFinite(anotado)) return anotado;
    return valorBrlLinha(linha, momento, cambioGlobal, { exato: true });
}

// Regra 3: valor na moeda do orçamento = R$ ÷ câmbio, arredondado uma vez.
export function moedaOrcamentoDeBrl(valorBrl = 0, cambioGlobal = 1) {
    const global = numero(cambioGlobal) > 0 ? numero(cambioGlobal) : 1;
    return centavos(numero(valorBrl) / global);
}

// Unitário calculado do FEE no modo R$ exato: 6 casas (precisão da coluna), sem o corte em 2 casas,
// para que o R$ do FEE possa ser refeito a partir do gravado (API, Resumo, Excel).
export const unitarioFeeExato = (valor = 0) => Math.round(numero(valor) * 1e6) / 1e6;
